import { isFilterActive } from './filterModel';
import type { ListFilterFieldDef, ListFilterValue, ListTemplateView } from './types';

/** Alias systémového pohledu „Vše" (prázdné filtry, nelze upravit ani smazat). */
export const ALL_VIEW_ID = '~all';

/** Alias šablonového pohledu — definice žije v kódu šablony, na server se neukládá. */
export function templateViewId(id: string): string {
  return `~tpl:${id}`;
}

/** Pohled v řádku pohledů — sjednocení systémového „Vše", šablonových a serverových pohledů. */
export interface ListViewDef {
  /** Stabilní id v UI (`~all`, `~tpl:{id}`, nebo Guid serverového pohledu). */
  id: string;
  label: string;
  filters: ListFilterValue[];
  /** Sdílený pohled (Team) — viditelný všem operátorům. */
  shared: boolean;
  /** Systémový pohled „Vše" — nelze přejmenovat, smazat ani upravit. */
  system: boolean;
  /** Šablonový pohled — definovaný kódem, needitovatelný. */
  template: boolean;
  /** Přihlášený uživatel je autor (smí upravit/smazat). */
  own: boolean;
  /** Display refs selector filtrů (z DisplayPayload) — mergují se při výběru pohledu. */
  displayRefs?: Record<string, unknown[]>;
}

/** Serverový pohled po dekódování payloadů. */
export interface DecodedServerView {
  id: string;
  name: string;
  shared: boolean;
  own: boolean;
  filters: ListFilterValue[];
  displayRefs?: Record<string, unknown[]>;
}

/**
 * Složí pole pohledů pro řádek záložek: systémové „Vše" + šablonové + serverové,
 * seřazené dle uživatelského `viewOrder` (neznámá id v pořadí se ignorují, nová id
 * se zařadí na konec v původním pořadí). „Vše" se řadí jako ostatní záložky — dokud ho
 * uživatel explicitně nepřesune (staré `viewOrder` bez `~all`), zůstává první. `allFilters`
 * jsou přišpendlené filtry ze specifikace (viz `defaultPinnedFilters`).
 */
export function buildViews(options: {
  allLabel: string;
  templateViews: ListTemplateView[];
  serverViews: DecodedServerView[];
  viewOrder?: string[];
  allFilters?: ListFilterValue[];
}): ListViewDef[] {
  const all: ListViewDef = {
    id: ALL_VIEW_ID,
    label: options.allLabel,
    filters: options.allFilters ?? [],
    shared: false,
    system: true,
    template: false,
    own: false,
  };

  // Pořadí kandidátů před aplikací `viewOrder` (výchozí = „Vše" první, pak šablonové, pak serverové).
  const everything: ListViewDef[] = [
    all,
    ...options.templateViews.map(
      (view): ListViewDef => ({
        id: templateViewId(view.id),
        label: view.label,
        filters: view.filters,
        shared: false,
        system: false,
        template: true,
        own: false,
      })
    ),
    ...options.serverViews.map(
      (view): ListViewDef => ({
        id: view.id,
        label: view.name,
        filters: view.filters,
        shared: view.shared,
        system: false,
        template: false,
        own: view.own,
        displayRefs: view.displayRefs,
      })
    ),
  ];

  const order = options.viewOrder ?? [];
  const byId = new Map(everything.map((view) => [view.id, view]));
  const ordered: ListViewDef[] = [];
  for (const id of order) {
    const view = byId.get(id);
    if (view) {
      ordered.push(view);
      byId.delete(id);
    }
  }
  const remaining = everything.filter((view) => byId.has(view.id));

  // „Vše" explicitně v pořadí → ctíme jeho pozici; jinak zůstává první (zpětná kompatibilita).
  if (order.includes(ALL_VIEW_ID)) {
    return [...ordered, ...remaining];
  }
  return [all, ...ordered, ...remaining.filter((view) => view.id !== ALL_VIEW_ID)];
}

/**
 * Signatura pohledu pro dirty detekci (anatomie 01 §2): neprázdné nebo připnuté filtry
 * s polem, operátorem, hodnotou a stavem připnutí, seřazené dle pole. `items` (display
 * data) ani pořadí filtrů do signatury nevstupují.
 */
export function viewSignature(
  filters: ListFilterValue[],
  defs: Map<string, ListFilterFieldDef>
): string {
  const significant = filters
    .filter((f) => isFilterActive(defs.get(f.field), f) || f.pinned)
    .map((f) => ({
      field: f.field,
      op: f.op ?? null,
      ids: f.ids ? [...f.ids].sort() : null,
      text: f.text?.trim() || null,
      number: f.number ?? null,
      from: f.from ?? null,
      to: f.to ?? null,
      pinned: Boolean(f.pinned),
    }))
    .sort((a, b) => a.field.localeCompare(b.field));
  return JSON.stringify(significant);
}
